import React from 'react';
import { Link } from 'react-router-dom';
import {
  FiArrowLeft,
  FiExternalLink,
  FiCpu,
  FiDatabase,
  FiCreditCard,
  FiMapPin,
  FiFileText,
  FiLock,
  FiArrowRight,
} from 'react-icons/fi';
import SEO from '../../components/SEO';
import './CaseStudy.css';

/**
 * HomeTaxProCaseStudy — first hand-crafted case study page under
 * /portfolio/hometaxpro. Uses the shared CaseStudy.css scaffolding
 * and sets its own --case-study-accent CSS custom property so all
 * accent tones (headline gradient, chip color, CTA panel gradient,
 * hover borders) key off one variable. Every future case study can
 * copy this file and only change the content + accent color.
 */
const ACCENT = '#5FE8B0';
const LIVE_URL = 'https://hometaxpro.com/';
const IMG_MAC =
  'https://silverfoxmedia.s3.amazonaws.com/htphomepageonmac.png';
const IMG_PHONE =
  "https://silverfoxmedia.s3.amazonaws.com/HomeTaxPro%20%E2%80%94%20America%27s%20%231%20Property%20Tax%20Protest%20Service%202-portrait.png";

const HomeTaxProCaseStudy = () => {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'CreativeWork',
    name: 'HomeTaxPro — Case Study',
    headline: 'AI-Powered Property Tax Protest Platform',
    description:
      "A property tax protest platform for homeowners. County appraisal + sales-comp scan, Anthropic Claude generates a filing-ready protest PDF, tiered Stripe subscriptions. Live in Texas DFW with a California pilot.",
    image: IMG_MAC,
    url: 'https://silverfoxmedia.co/portfolio/hometaxpro',
    author: {
      '@type': 'Organization',
      name: 'SilverFox Media',
      url: 'https://silverfoxmedia.co',
    },
    about: [
      { '@type': 'Thing', name: 'Property Tax Protest' },
      { '@type': 'Thing', name: 'MERN Stack' },
      { '@type': 'Thing', name: 'Anthropic Claude' },
      { '@type': 'Thing', name: 'Stripe Subscriptions' },
    ],
  };

  return (
    <>
      <SEO
        title="HomeTaxPro Case Study: AI Property Tax Protest Platform"
        description="How SilverFox Media designed and shipped HomeTaxPro: an AI-powered property tax protest platform that scans county appraisal + comps, generates a filing-ready Claude-authored PDF, and processes tiered Stripe subscriptions. Live in Texas DFW with a California pilot."
        keywords="HomeTaxPro case study, property tax protest platform, MERN stack case study, Anthropic Claude, Stripe subscriptions, ATTOM Data, MongoDB Atlas, React Vite, Dallas web development"
        image="https://silverfoxmedia.co/images/og/hometaxpro.jpg"
        url="/portfolio/hometaxpro"
        type="article"
        schema={structuredData}
      />

      <article
        className="case-study"
        style={{ '--case-study-accent': ACCENT }}
      >
        {/* HERO — 2-column on desktop (text left, media right).
            On mobile the hero-media block is hidden via CSS and the
            standalone Featured Image section below handles the visual. */}
        <section className="case-study__hero">
          <div className="container">
            <div className="case-study__hero-grid">
              <div className="case-study__hero-inner">
                <Link to="/portfolio" className="case-study__back">
                  <FiArrowLeft />
                  Back to Portfolio
                </Link>

                <span className="case-study__category">Web Application</span>

                <h1 className="case-study__title">
                  HomeTaxPro:{' '}
                  <span className="case-study__title-accent">
                    AI-Powered Property Tax Protest
                  </span>
                </h1>

                <p className="case-study__tagline">
                  A national-positioned platform that turns a homeowner's address into a
                  filing-ready property tax protest packet — county appraisal scan,
                  comparable-sales analysis, and an Anthropic Claude–authored PDF, all
                  behind a tiered Stripe subscription. Live in Texas DFW with a California
                  pilot in Orange County.
                </p>

                <div className="case-study__hero-cta">
                  <a
                    href={LIVE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="case-study__cta-primary"
                  >
                    Visit Live Site <FiExternalLink />
                  </a>
                  <Link to="/contact" className="case-study__cta-secondary">
                    Start Your Project <FiArrowRight />
                  </Link>
                </div>

                <div className="case-study__hero-meta">
                  <div className="case-study__hero-meta-item">
                    <span className="case-study__hero-meta-label">Client</span>
                    <span className="case-study__hero-meta-value">HomeTaxPro</span>
                  </div>
                  <div className="case-study__hero-meta-item">
                    <span className="case-study__hero-meta-label">Industry</span>
                    <span className="case-study__hero-meta-value">Property Tax / Real Estate</span>
                  </div>
                  <div className="case-study__hero-meta-item">
                    <span className="case-study__hero-meta-label">Timeline</span>
                    <span className="case-study__hero-meta-value">2026 — Ongoing</span>
                  </div>
                  <div className="case-study__hero-meta-item">
                    <span className="case-study__hero-meta-label">Role</span>
                    <span className="case-study__hero-meta-value">Full-Stack Build</span>
                  </div>
                </div>
              </div>

              <div className="case-study__hero-media">
                <img
                  src={IMG_MAC}
                  alt="HomeTaxPro homepage displayed on a MacBook mockup"
                  className="case-study__hero-macbook"
                  loading="lazy"
                />
                <img
                  src={IMG_PHONE}
                  alt="HomeTaxPro property scan flow on a portrait mobile screen"
                  className="case-study__hero-phone"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>

        {/* FEATURED IMAGE */}
        <section className="case-study__featured">
          <div className="container">
            <div className="case-study__featured-image-wrap">
              <img
                src={IMG_MAC}
                alt="HomeTaxPro homepage displayed on a MacBook mockup"
                className="case-study__featured-image"
                loading="lazy"
              />
              <img
                src={IMG_PHONE}
                alt="HomeTaxPro property scan flow on a portrait mobile screen"
                className="case-study__featured-phone"
                loading="lazy"
              />
            </div>
          </div>
        </section>

        {/* OVERVIEW */}
        <section className="case-study__section">
          <div className="container">
            <p className="case-study__section-label">The Overview</p>
            <h2 className="case-study__section-title">
              From an address to a filing-ready protest, in one flow.
            </h2>
            <div className="case-study__prose">
              <p>
                Every year, most homeowners overpay their property tax bill because filing
                a protest is opaque, slow, and reserved for people who understand county
                appraisal math. HomeTaxPro flips that. A homeowner enters an address,
                receives a free scan against the county's appraisal record and recent
                comparable sales, subscribes, claims the property, and downloads a
                filing-ready protest PDF generated by an Anthropic Claude analysis run on
                demand.
              </p>
              <p>
                The platform is national in positioning but pilots county-by-county:
                Texas DFW (Collin, Dallas, Tarrant, Denton) is live; an Orange County,
                California pilot is prepping around the Sep 15 appeal deadline. The
                California cohort is narrower on purpose — Proposition 13's assessment
                ceiling protects most owners, so the platform enumerates ATTOM Data
                transactions to isolate 2020–2022 buyers with a viable Prop 8
                decline-in-value angle.
              </p>
            </div>
          </div>
        </section>

        {/* CHALLENGE */}
        <section className="case-study__section" style={{ background: 'var(--color-gray-100)' }}>
          <div className="container">
            <p className="case-study__section-label">The Challenge</p>
            <h2 className="case-study__section-title">
              Consumer trust, county data mess, and an AI cost ceiling.
            </h2>
            <div className="case-study__prose">
              <p>
                Three problems shaped every architectural decision. First, homeowners
                don't trust property-tax services — the incumbent model is a
                contingency-fee middleman that takes 30–50% of savings. HomeTaxPro
                positions on a flat annual subscription instead ($49 / $99 / $109), so
                the platform earns before knowing whether the protest wins and the
                homeowner keeps 100% of any reduction. The pricing model had to be
                honest and legible, and the UX had to signal that upfront.
              </p>
              <p>
                Second, county appraisal and sales-comp data is fragmented across state
                systems with inconsistent schemas, uneven update cadence, and no unified
                API. Ingest had to be pipeline-per-county, resilient to source-format
                drift, and produce a normalized MongoDB projection accurate enough that
                the free-scan preview held credibility against real comps.
              </p>
              <p>
                Third, running Claude on every homepage visit would burn the margin.
                AI analysis is expensive relative to the subscription price, so the
                platform had to gate the Claude call behind a claim event — free scan
                is deterministic county-lookup + kNN comparable search, Claude only
                fires when a paid subscriber claims a specific property.
              </p>
            </div>
          </div>
        </section>

        {/* SOLUTION / KEY FEATURES */}
        <section className="case-study__section">
          <div className="container">
            <p className="case-study__section-label">The Solution</p>
            <h2 className="case-study__section-title">
              A MERN stack, one Render service, and Claude on the hot path.
            </h2>
            <p className="case-study__section-lead">
              HomeTaxPro is a plain MERN + Vite build with no framework wrapper.
              Auth is server-side sessions in Mongo (no JWT overhead), payments are
              Stripe Checkout + webhooks mirroring subscription state, and Claude is
              wired via the Anthropic SDK's messages API with tool use — only on
              paid, claim-gated /ai-review calls.
            </p>

            <div className="case-study__features">
              <div className="case-study__feature">
                <span className="case-study__feature-icon"><FiCpu /></span>
                <h3 className="case-study__feature-title">Claude-Authored Protest PDFs</h3>
                <p className="case-study__feature-body">
                  Anthropic Claude via <code>messages.create</code> analyzes the parcel
                  data + comparable sales and drafts a filing-ready protest PDF with
                  retry/backoff. Only runs on paid claim events to control token spend.
                </p>
              </div>

              <div className="case-study__feature">
                <span className="case-study__feature-icon"><FiCreditCard /></span>
                <h3 className="case-study__feature-title">Tiered Stripe Subscriptions</h3>
                <p className="case-study__feature-body">
                  Three auto-renewing annual tiers ($49 / $99 / $109) built on Stripe
                  Checkout. Webhook handler mirrors subscription state into a Mongo
                  <code>subscriptions</code> collection. $50 add-property one-time
                  charges reuse the same integration in payment mode.
                </p>
              </div>

              <div className="case-study__feature">
                <span className="case-study__feature-icon"><FiDatabase /></span>
                <h3 className="case-study__feature-title">County Ingest Pipelines</h3>
                <p className="case-study__feature-body">
                  Per-county ingest jobs normalize Parcel, Evaluation, and County
                  collections into MongoDB Atlas. Collin County (TX) shipped with
                  97.2% residential centroid coverage; Orange County (CA) is next.
                </p>
              </div>

              <div className="case-study__feature">
                <span className="case-study__feature-icon"><FiMapPin /></span>
                <h3 className="case-study__feature-title">ATTOM Targeting Logic</h3>
                <p className="case-study__feature-body">
                  California acquisition enumerates ATTOM Data <code>/sale/snapshot</code>
                  transactions to isolate 2020–2022 Orange County buyers — the only
                  cohort with a viable Prop 8 decline-in-value protest inside Prop 13's
                  assessment ceiling.
                </p>
              </div>

              <div className="case-study__feature">
                <span className="case-study__feature-icon"><FiLock /></span>
                <h3 className="case-study__feature-title">Session-Based Auth</h3>
                <p className="case-study__feature-body">
                  httpOnly <code>htp_session</code> cookie backed by a Mongo
                  <code>sessions</code> collection — no JWT. bcryptjs password hashing,
                  SendGrid magic-link email verification, and password reset via one
                  shared email service.
                </p>
              </div>

              <div className="case-study__feature">
                <span className="case-study__feature-icon"><FiFileText /></span>
                <h3 className="case-study__feature-title">Single Render Deploy</h3>
                <p className="case-study__feature-body">
                  Express serves <code>/api/*</code> and the compiled React bundle from
                  <code>frontend/dist</code> as one Render web service — no separate
                  frontend host, no CORS overhead, environment-scoped secrets for
                  Anthropic, Stripe, SendGrid, and ATTOM.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* TECH STACK */}
        <section className="case-study__section" style={{ background: 'var(--color-gray-100)' }}>
          <div className="container">
            <p className="case-study__section-label">The Stack</p>
            <h2 className="case-study__section-title">Built on MERN + Vite, Node 22, ESM.</h2>
            <p className="case-study__section-lead">
              Plain JavaScript, no TypeScript, no framework wrapper. Every dependency
              earns its place.
            </p>

            <div className="case-study__tech">
              <span className="case-study__tech-chip">React 19</span>
              <span className="case-study__tech-chip">Vite</span>
              <span className="case-study__tech-chip">Tailwind CSS</span>
              <span className="case-study__tech-chip">Node.js 22</span>
              <span className="case-study__tech-chip">Express 4</span>
              <span className="case-study__tech-chip">MongoDB Atlas</span>
              <span className="case-study__tech-chip">Mongoose</span>
              <span className="case-study__tech-chip">Anthropic Claude</span>
              <span className="case-study__tech-chip">Stripe Checkout + Webhooks</span>
              <span className="case-study__tech-chip">SendGrid</span>
              <span className="case-study__tech-chip">ATTOM Data</span>
              <span className="case-study__tech-chip">bcryptjs</span>
              <span className="case-study__tech-chip">Render</span>
            </div>
          </div>
        </section>

        {/* RESULTS */}
        <section className="case-study__section case-study__section--dark">
          <div className="container">
            <p className="case-study__section-label">The Results</p>
            <h2 className="case-study__section-title">Shipped, indexed, and prepped for a pilot.</h2>
            <p className="case-study__section-lead" style={{ color: 'rgba(255,255,255,0.75)' }}>
              HomeTaxPro is live at hometaxpro.com — hosted on Render, indexed by
              Google, and ready for the California cohort push around the Sep 15
              appeal deadline.
            </p>

            <div className="case-study__stats">
              <div className="case-study__stat">
                <p className="case-study__stat-value">4 counties</p>
                <p className="case-study__stat-label">DFW live coverage (Collin, Dallas, Tarrant, Denton)</p>
              </div>
              <div className="case-study__stat">
                <p className="case-study__stat-value">97.2%</p>
                <p className="case-study__stat-label">Residential centroid coverage post-ingest</p>
              </div>
              <div className="case-study__stat">
                <p className="case-study__stat-value">100%</p>
                <p className="case-study__stat-label">Savings kept by homeowner (flat annual model)</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="case-study__section">
          <div className="container">
            <div className="case-study__cta">
              <h2 className="case-study__cta-title">Have a build like this in mind?</h2>
              <p className="case-study__cta-text">
                MERN, AI on the hot path, Stripe subscriptions, county-scale data
                ingest — we build applications like HomeTaxPro end-to-end. Let's talk.
              </p>
              <Link to="/contact" className="case-study__cta-btn">
                Start Your Project <FiArrowRight />
              </Link>
            </div>
          </div>
        </section>
      </article>
    </>
  );
};

export default HomeTaxProCaseStudy;
